import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Home, Wrench, Phone, Users, HelpCircle } from 'lucide-react';

const ScrollProgressNavigation = ({ isDarkMode }) => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isMobile, setIsMobile] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Sections konfigurace - stabilní pomocí useMemo
  const sections = useMemo(() => [
    { id: 'hero', label: 'Úvod', Icon: Home },
    { id: 'services', label: 'Služby', Icon: Wrench },
    { id: 'why-us', label: 'Proč my', Icon: HelpCircle },
    { id: 'contact', label: 'Kontakt', Icon: Phone },
    { id: 'about', label: 'O nás', Icon: Users },
  ], []);

  useEffect(() => {
    const updateScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const updateScrollProgress = () => {
      const currentScroll = window.pageYOffset;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(currentScroll / Math.max(scrollHeight, 1), 1);
      setScrollProgress(progress);
    };

    const findActiveSection = () => {
      const scrollPosition = window.pageYOffset + 100;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Kontrola, zda jsme na konci stránky (s malou rezervou)
      const isNearBottom = (window.pageYOffset + windowHeight) >= (documentHeight - 50);
      
      if (isNearBottom) {
        // Pokud jsme na konci stránky, označme poslední sekci
        const lastSection = sections[sections.length - 1];
        if (activeSection !== lastSection.id) {
          setActiveSection(lastSection.id);
        }
        return;
      }
      
      // Standardní logika pro detekci aktivní sekce
      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i].id);
        if (element && element.offsetTop <= scrollPosition) {
          if (activeSection !== sections[i].id) {
            setActiveSection(sections[i].id);
          }
          break;
        }
      }
    };

    // Event listeners
    updateScreenSize();
    updateScrollProgress();
    findActiveSection();
    
    window.addEventListener('scroll', updateScrollProgress, { passive: true });
    window.addEventListener('scroll', findActiveSection, { passive: true });
    window.addEventListener('resize', updateScreenSize, { passive: true });

    return () => {
      window.removeEventListener('scroll', updateScrollProgress);
      window.removeEventListener('scroll', findActiveSection);
      window.removeEventListener('resize', updateScreenSize);
    };
  }, [activeSection, sections]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  const navigationComponent = (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ease-out bg-white/85 backdrop-blur-md shadow-lg rounded-full border border-gray-200/60 ${isMobile ? 'px-3 pt-3 pb-2' : 'px-6 pt-4 pb-3'} will-change-transform`}
      style={{
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        boxShadow: `
          0 8px 32px rgba(0, 0, 0, 0.12),
          inset 0 1px 0 rgba(255, 255, 255, 0.2),
          inset 0 -1px 0 rgba(255, 255, 255, 0.1)
        `,
      }}
    >
      {/* Progress bar */}
      <div className="absolute top-0.5 left-6 right-6 h-1.5 bg-gray-200/50 overflow-hidden rounded-full">
        <div
          className="h-full bg-red-500 transition-all duration-300 ease-out rounded-full"
          style={{ width: `${scrollProgress * 100}%` }}
        />
      </div>

      {/* Navigation buttons with labels */}
      <div className={`flex items-center ${isMobile ? 'space-x-0' : 'space-x-1'}`}>
        {sections.map((section, index) => {
          const isActive = activeSection === section.id;
          
          return (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className={`
                group relative transition-all duration-300 ease-out
                flex ${isMobile ? 'items-center' : 'items-center space-x-2'} 
                ${isMobile ? 'px-2.5 py-2' : 'px-4 py-2'} rounded-full
                ${isActive
                  ? 'bg-red-500/15 text-red-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-800 hover:bg-gray-900/8'
                }
              `}
              title={section.label}
            >
              <section.Icon size={isMobile ? 16 : 16} className="flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
              {!(isMobile && section.id === 'hero') && (
                <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium whitespace-nowrap`}>
                  {section.label}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  return createPortal(navigationComponent, document.body);
};

export default ScrollProgressNavigation;
